import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
} from '@mui/material';
import Dropdown from '../../ui/Dropdown';
import MeasureList from './MeasureList';
import { components } from '../../api/types';

type FilterParams = components["schemas"]["FilterParams"];
type Quantiles = components["schemas"]["Quantiles"] | null;

type Props = {
  /* Measure data */
  measureNames: string[],
  measureCorrelationPercents: number[],
  predictedScores: number[],
  predictedStds: number[],
  predictedPercentileRanks: (number | null)[],

  /* Diagnosis data */
  diagnosisNames: string[],
  actualDiagnosisMask: boolean[],
  diagnosisColors: string[],
  quantiles1: Quantiles[],
  quantiles2: Quantiles[],
  setFilterParams1: React.Dispatch<React.SetStateAction<FilterParams>>,
  setFilterParams2: React.Dispatch<React.SetStateAction<FilterParams>>,

  loading: boolean,
};

/**
 * Predicted diagnoses page component
 * 
 * @param selectedId: string - selected client ID
 * @param loading: boolean - loading state
 * 
 * @returns Predicted diagnoses page element
 */
export const ConfirmationTestsPage = ({
  /* Measure data */
  measureNames,
  measureCorrelationPercents,
  predictedScores,
  predictedStds,
  predictedPercentileRanks,

  /* Diagnosis data */
  diagnosisNames,
  actualDiagnosisMask,
  diagnosisColors,
  quantiles1,
  quantiles2,
  setFilterParams1,
  setFilterParams2,

  loading,
}: Props): JSX.Element => {

  const noDiagnosisText = 'No Diagnosis';
  const noDiagnosisColor = '#aaaaaa';
  const diagnosisNameOptions = [...diagnosisNames, noDiagnosisText];
  const diagnosisColorOptions = [...diagnosisColors, noDiagnosisColor];
  const [selectedDiagnosisName1, setSelectedDiagnosisName1] = useState<string>(noDiagnosisText);
  const [selectedDiagnosisName2, setSelectedDiagnosisName2] = useState<string>(noDiagnosisText);
  const diagnosisColor1 = diagnosisColorOptions[diagnosisNameOptions.indexOf(selectedDiagnosisName1)];
  const diagnosisColor2 = diagnosisColorOptions[diagnosisNameOptions.indexOf(selectedDiagnosisName2)];

  /* Initialize diagnosis names when data loads */
  useEffect(() => {
    if (diagnosisNames.length > 0) {
      const firstActualIndex = actualDiagnosisMask.findIndex(mask => mask);
      const initialDiagnosis2 = firstActualIndex !== -1 ? diagnosisNames[firstActualIndex] : diagnosisNames[0];
      setSelectedDiagnosisName2(initialDiagnosis2);
    }
  }, [diagnosisNames, actualDiagnosisMask]);

  /* Update filters */
  useEffect(() => {
    if (!selectedDiagnosisName1 || !selectedDiagnosisName2) return;
    const filterParams1 = {
      equal: {
        ...(selectedDiagnosisName1 !== noDiagnosisText && { [selectedDiagnosisName1]: true }),
        ...(selectedDiagnosisName2 !== noDiagnosisText && { [selectedDiagnosisName2]: false }),
      }
    };
    const filterParams2 = {
      equal: {
        ...(selectedDiagnosisName1 !== noDiagnosisText && { [selectedDiagnosisName1]: false }),
        ...(selectedDiagnosisName2 !== noDiagnosisText && { [selectedDiagnosisName2]: true }),
      }
    };
    flushSync(() => {
      setFilterParams1(filterParams1);
      setFilterParams2(filterParams2);
    });
  }, [selectedDiagnosisName1, selectedDiagnosisName2, setFilterParams1, setFilterParams2]);

  // if (loading) {
  //   return (
  //     <>
  //       <h1 className="text">Confirmation Tests</h1>
  //       <p className="text content">Loading...</p>
  //     </>
  //   );
  // }

  return (
    <>
      <h1 className="text">Confirmation Tests</h1>
      <p className="text content">To help refine the diagnosis, you can compare InsightCare's prediction of the client's test scores with two populations:</p>
      <span className="diagnosisSelectContainer">

        <Dropdown
          availableNames={diagnosisNameOptions}
          availableValues={diagnosisNameOptions}
          selectedValue={selectedDiagnosisName1}
          setSelectedValue={setSelectedDiagnosisName1}
          loading={diagnosisNames.length === 0}
          selectStyleOverrides={{ 
            color: diagnosisColor1,
            fontWeight: 'bold',
          }}
          itemStyleOverrides={
            diagnosisColorOptions.map((color) => ({
              color: color,
              fontWeight: 'bold',
            }))
          }
        />

        <span className="vsText">vs.</span>

        <Dropdown
          availableNames={diagnosisNameOptions}
          availableValues={diagnosisNameOptions}
          selectedValue={selectedDiagnosisName2}
          setSelectedValue={setSelectedDiagnosisName2}
          loading={diagnosisNames.length === 0}
          selectStyleOverrides={{ 
            color: diagnosisColor2,
            fontWeight: 'bold',
          }}
          itemStyleOverrides={
            diagnosisColorOptions.map((color) => ({
              color: color,
              fontWeight: 'bold',
            }))
          }
        />

      </span>
      { loading
        ?
        <p className="text content">Loading...</p>
        :
        ( selectedDiagnosisName1 === selectedDiagnosisName2
          ?
          <p className="text content">Please select two different diagnoses.</p>
          :
          <>
            <p className="text content">These are the top measures the client has yet to take to help rule out potential diagnoses:</p>
            <MeasureList
              measureNames={measureNames}
              measureCorrelationPercents={measureCorrelationPercents}
              predictedScores={predictedScores}
              predictedStds={predictedStds}
              predictedPercentileRanks={predictedPercentileRanks}
              diagnosisName1={selectedDiagnosisName1}
              diagnosisName2={selectedDiagnosisName2}
              diagnosisColor1={diagnosisColor1}
              diagnosisColor2={diagnosisColor2}
              quantiles1={quantiles1}
              quantiles2={quantiles2}
            />
          </>
        )
      }
    </>
  );
};

export default ConfirmationTestsPage;